/**
 * WakeUpRitualScreen — 3-frame monastic entry for scheduled retreats.
 * Frames: before (T-2min) → bell (T=0) → awake (user-tapped).
 * UI-INV-005: Leaf component, props only.
 * Task 10 (Session E).
 */
import { useState, useEffect, useRef } from "react";
import styles from "./WakeUpRitualScreen.module.css";
import { IntentStrip } from "../components/IntentStrip";
import type { EditableRetreatSchedule } from "@retreat/core";

function getScheduleMeta(meta: unknown): { teacher: string; tradition: string } {
  if (typeof meta === "object" && meta !== null) {
    const m = meta as Record<string, unknown>;
    return {
      teacher: typeof m.teacher === "string" ? m.teacher : "",
      tradition: typeof m.tradition === "string" ? m.tradition : "",
    };
  }
  return { teacher: "", tradition: "" };
}

type RitualFrame = "before" | "bell" | "awake";

type Props = {
  intentLabels: string[];
  schedule: EditableRetreatSchedule | null;
  lastBellFiredAt: number | null;
  whisperLine?: string;
  onBeginSitting: () => void;
  todayLabel?: string;
};

export function WakeUpRitualScreen({
  intentLabels,
  schedule,
  lastBellFiredAt,
  whisperLine = "The stillness before sitting is part of the sitting.",
  onBeginSitting,
  todayLabel,
}: Props) {
  const [frame, setFrame] = useState<RitualFrame>("before");

  // Capture lastBellFiredAt at mount time.
  // If it was already non-null on mount, the bell fired before we rendered — go straight to bell frame.
  const mountBellFiredAt = useRef(lastBellFiredAt);

  // On mount: if bell already fired before this component mounted, skip to bell frame
  useEffect(() => {
    if (mountBellFiredAt.current !== null) {
      setFrame("bell");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After mount: if lastBellFiredAt changes to a new non-null value, transition to bell frame
  useEffect(() => {
    if (
      lastBellFiredAt !== null &&
      lastBellFiredAt !== mountBellFiredAt.current &&
      frame === "before"
    ) {
      setFrame("bell");
    }
  }, [lastBellFiredAt, frame]);

  // Derive schedule preview (first 3 containers of day 1)
  const schedulePreview = schedule?.days[0]?.containers.slice(0, 3) ?? [];

  if (frame === "before") {
    return (
      <div className={styles.before} data-testid="wake-ritual-screen" data-frame="before">
        <div className={styles.beforeMeta}>
          <span className={styles.beforeDate} />
        </div>
        <div className={styles.beforeLine} />
        <p className={styles.beforeEyebrow}>The room is preparing</p>
        <p className={styles.beforeWhisper}>{whisperLine}</p>
        <p className={styles.beforeFooter}>
          You don't have to do anything yet.
        </p>
      </div>
    );
  }

  if (frame === "bell") {
    return (
      <div
        className={styles.bell}
        data-testid="wake-ritual-screen"
        data-frame="bell"
        onClick={() => setFrame("awake")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFrame("awake"); } }}
        aria-label="Tap to continue"
      >
        <div className={styles.bellRings}>
          <div className={styles.bellRingOuter} />
          <div className={styles.bellRingInner} />
          <div className={styles.bellDot} />
        </div>
        <h1 className={styles.bellHeading}>It is time to sit.</h1>
        <p className={styles.bellSub}>Don't hurry.</p>
        {schedule && (() => {
          const { teacher, tradition } = getScheduleMeta(schedule.metadata);
          return (teacher || tradition) ? (
            <p className={styles.bellTradition}>
              {teacher} · {tradition}
            </p>
          ) : null;
        })()}
        <p className={styles.bellFooter}>tap when you are awake</p>
      </div>
    );
  }

  // Frame 3: Awake
  const nowTime = todayLabel ? `${todayLabel} · awake` : "awake";
  return (
    <div className={styles.awake} data-testid="wake-ritual-screen" data-frame="awake">
      <div className={styles.awakeMeta}>
        <span className={styles.awakeTime}>{nowTime}</span>
      </div>
      <IntentStrip intents={intentLabels} style={{ margin: "8px 22px 0" }} />
      <div className={styles.awakeContent}>
        <h1 className={styles.awakeHeading}>Take three breaths. Then sit.</h1>
        <p className={styles.awakeWhisper}>
          The first thirty minutes are seated. The bell will sound when it's time to walk.
        </p>

        {/* Schedule preview */}
        {schedulePreview.length > 0 && (
          <div className={styles.scheduleCard} data-testid="wake-ritual-schedule-preview">
            {schedulePreview.map((container, idx) => (
              <div
                key={container.id ?? idx}
                className={`${styles.scheduleRow} ${idx === 0 ? styles.scheduleRowNow : ""}`}
              >
                {idx === 0 && <span className={styles.nowMarker}>▸</span>}
                <span className={styles.containerName}>{container.name}</span>
                <span className={styles.containerDur}>
                  {Math.round(container.durationSeconds / 60)}m
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          className={styles.beginCta}
          onClick={onBeginSitting}
          type="button"
          data-testid="begin-sitting-btn"
        >
          Sit
        </button>
      </div>
    </div>
  );
}
